import {
  CatalogSortSchema,
  DirectionSchema,
  OccasionSchema,
  ProductTypeSchema,
  type CatalogQuery,
  type Occasion,
  type ProductType,
} from '../model/product';

type Params = Record<string, string | string[] | undefined>;

const first = (value: string | string[] | undefined) =>
  (Array.isArray(value) ? value[0] : value) ?? null;
const list = (value: string | string[] | undefined) =>
  (Array.isArray(value) ? value : value ? value.split(',') : [])
    .map((item) => item.trim())
    .filter(Boolean);

export const EMPTY_CATALOG_QUERY: CatalogQuery = {
  direction: null,
  occasions: [],
  types: [],
  size: null,
  color: null,
  availability: null,
  sort: 'default',
};

/** Tolerant URL → query parsing: unknown values are ignored instead of failing the page. */
export function parseCatalogQuery(params: Params): CatalogQuery {
  const direction = DirectionSchema.safeParse(first(params.direction));
  const sort = CatalogSortSchema.safeParse(first(params.sort));
  const availability = first(params.availability);
  const size = first(params.size);
  const color = first(params.color);
  return {
    direction: direction.success ? direction.data : null,
    occasions: list(params.occasion).filter(
      (item): item is Occasion => OccasionSchema.safeParse(item).success,
    ),
    types: list(params.type).filter(
      (item): item is ProductType => ProductTypeSchema.safeParse(item).success,
    ),
    size: size && /^[A-Z0-9]{1,4}$/.test(size) ? size : null,
    color: color && /^[a-z-]{2,32}$/.test(color) ? color : null,
    availability: availability === 'in_stock' || availability === 'preorder' ? availability : null,
    sort: sort.success ? sort.data : 'default',
  };
}

/** Query → "?direction=body&occasion=practice,home" (empty string when nothing is set). */
export function catalogQueryToSearch(query: CatalogQuery): string {
  const params = new URLSearchParams();
  if (query.direction) params.set('direction', query.direction);
  if (query.occasions.length) params.set('occasion', query.occasions.join(','));
  if (query.types.length) params.set('type', query.types.join(','));
  if (query.size) params.set('size', query.size);
  if (query.color) params.set('color', query.color);
  if (query.availability) params.set('availability', query.availability);
  if (query.sort !== 'default') params.set('sort', query.sort);
  const search = params.toString();
  return search ? `?${search}` : '';
}

export function isCatalogQueryEmpty(query: CatalogQuery): boolean {
  return catalogQueryToSearch({ ...query, sort: 'default' }) === '';
}
